/**
 * SoulumoHeader — the single top navigation used on every public
 * Soulumo route. Mar 6 2026.
 *
 * Desktop (≥ 640px): full menu bar visible.
 * Mobile: wordmark left, hamburger right → slide-out from the right.
 *
 * The menu adapts to auth state:
 *   Authed   → Home · Library · Why Soulumo · Account
 *   Unauthed → Home · Why Soulumo · Sign in · Begin (gold CTA)
 */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import SoulumoWord from "./SoulumoWord";
import { ByReclaim } from "./ecosystem/ReclaimNav";

const TOK = {
  cream: "#f5e0ad",
  creamDim: "rgba(245,224,173,0.72)",
  creamMist: "rgba(245,224,173,0.42)",
  gold: "#e7c98a",
  goldSoft: "rgba(231,201,138,0.86)",
  goldMist: "rgba(231,201,138,0.24)",
  ink: "#0d1f3a",
};

const isAuthed = () => Boolean(
  typeof window !== "undefined" && window.localStorage.getItem("reclaim_token")
);

// Back within the app when there is history, otherwise to a known Soulumo page.
export const goBack = (nav, fallback = "/soulumo") => ((window.history.state?.idx ?? 0) > 0 ? nav(-1) : nav(fallback));

export default function SoulumoHeader() {
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [authed, setAuthed] = useState(isAuthed());

  // Refresh auth flag on route change (sign-in / sign-out flips it).
  useEffect(() => { setAuthed(isAuthed()); setOpen(false); }, [loc.pathname]);

  const items = authed
    ? [
        // Explicit Soulumo routes — "/" and "/account" are brand-routed and
        // fall back to Reclaim on non-Soulumo hosts (e.g. the preview link).
        { label: "Create",       path: "/my-installations" },
        { label: "Signatures",   path: "/signature-installations" },
        { label: "Library",      path: "/my-library" },
        { label: "My voice",     path: "/voice-library" },
        { label: "Coaches",      path: "/soulumo/coach" },
        { label: "Account",      path: "/soulumo/account" },
        { label: "Why Soulumo",  path: "/soulumo/why" },
      ]
    : [
        { label: "Home",         path: "/soulumo" },
        { label: "Why Soulumo",  path: "/soulumo/why" },
        { label: "For coaches",  path: "/soulumo/for-coaches" },
        { label: "Sign in",      path: "/soulumo/join?mode=signin" },
      ];

  const isActive = (p) => (p === "/soulumo" ? loc.pathname === p : loc.pathname.startsWith(p));

  return (
    <>
      {createPortal(<>
      <header
        className="fixed top-0 left-0 right-0 z-[110]"
        style={{
          // Subtle glass — deep navy underneath, backdrop blur, and
          // a hairline of gold at the very bottom edge. No glow, no
          // shadow. Deliberately quiet.
          background: "rgba(10,19,42,0.86)",
          backdropFilter: "saturate(140%) blur(18px)",
          WebkitBackdropFilter: "saturate(140%) blur(18px)",
          borderBottom: "1px solid rgba(231,201,138,0.14)",
        }}
        data-testid="soulumo-header"
      >
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
          <span className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => nav(authed ? "/my-installations" : "/soulumo")}
              data-testid="soulumo-header-word"
              className="flex-shrink-0"
              aria-label="Soulumo — Home"
            >
              <SoulumoWord size="xs" />
            </button>
            <ByReclaim product="soulumo" showName={false} className="hidden min-[360px]:inline-flex" />
          </span>

          {/* Desktop menu */}
          <nav className="hidden sm:flex items-center gap-7">
            {items.map((it) => (
              <button
                key={it.path}
                onClick={() => nav(it.path)}
                data-testid={`soulumo-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="uppercase transition-colors pb-1"
                style={{
                  borderBottom: `1px solid ${isActive(it.path) ? TOK.gold : "transparent"}`,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  letterSpacing: "0.26em",
                  color: isActive(it.path) ? TOK.gold : TOK.cream,
                }}
              >
                {it.label}
              </button>
            ))}
            {!authed && (
              <button
                onClick={() => nav("/soulumo/join")}
                data-testid="soulumo-nav-begin"
                className="rounded-full px-5 py-2 transition-colors"
                style={{
                  background: "transparent",
                  border: `1px solid ${TOK.goldMist}`,
                  color: TOK.cream,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.64rem",
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                }}
              >
                · Begin ·
              </button>
            )}
          </nav>

          {/* Mobile hamburger — minimal glass */}
          <button
            onClick={() => setOpen(true)}
            data-testid="soulumo-header-hamburger"
            className="sm:hidden inline-flex items-center justify-center rounded-full w-9 h-9 transition-colors"
            style={{
              background: "transparent",
              border: "1px solid rgba(231,201,138,0.14)",
              color: TOK.cream,
            }}
            aria-label="Open menu"
          >
            <Menu size={15} />
          </button>
        </div>
      </header>


      {/* Mobile slide-out */}
      {open && (
        <div
          className="fixed inset-0 z-[120] sm:hidden"
          data-testid="soulumo-header-drawer"
          onClick={() => setOpen(false)}
        >
          <div
            className="absolute inset-0"
            style={{ background: "rgba(3,7,16,0.82)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}
          />
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute right-0 top-0 bottom-0 w-[82vw] max-w-xs flex flex-col shadow-2xl"
            style={{
              background: "#0b1a33",
              borderLeft: `1px solid ${TOK.goldMist}`,
            }}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <SoulumoWord size="xs" />
              <button
                onClick={() => setOpen(false)}
                data-testid="soulumo-header-drawer-close"
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: "transparent", border: `1px solid ${TOK.goldMist}`, color: TOK.cream }}
                aria-label="Close menu"
              >
                <X size={14} />
              </button>
            </div>
            <nav className="flex-1 px-5 pt-6 space-y-1">
              {items.map((it) => (
                <button
                  key={it.path}
                  onClick={() => { setOpen(false); nav(it.path); }}
                  data-testid={`soulumo-nav-mobile-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="w-full text-left rounded-xl px-4 py-3 flex items-center justify-between"
                  style={{
                    background: isActive(it.path) ? "rgba(231,201,138,0.10)" : "transparent",
                    border: `1px solid ${isActive(it.path) ? TOK.goldMist : "transparent"}`,
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "1.3rem",
                    fontWeight: 500,
                    color: isActive(it.path) ? "#fff6e0" : TOK.cream,
                  }}
                >
                  <span>{it.label}</span>
                  <span style={{ color: TOK.goldSoft, fontSize: "0.85rem" }}>→</span>
                </button>
              ))}
            </nav>
            {!authed && (
              <div className="px-5 pb-8">
                <button
                  onClick={() => { setOpen(false); nav("/soulumo/join"); }}
                  data-testid="soulumo-nav-mobile-begin"
                  className="w-full rounded-full px-5 py-3 transition-all"
                  style={{
                    background: TOK.gold,
                    color: TOK.ink,
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.72rem",
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                  }}
                >
                  · Begin ·
                </button>
              </div>
            )}
            <div
              className="text-center pb-6 text-[0.52rem] uppercase tracking-[0.32em]"
              style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
            >
              Soulumo · v0.1
            </div>
          </div>
        </div>
      )}
      </>, document.body)}
      {/* Spacer so page content isn't hidden under the fixed header */}
      <div aria-hidden style={{ height: 56 }} data-testid="soulumo-header-spacer" />
    </>
  );
}
